/**
 * Private documents (IC, licence, slips, receipts, signed papers...) are served by the API at
 * /api/files/{media_id}/{file_name}, only to the signed-in buyer they belong to, so they are
 * fetched with the login (axios carries the Bearer token set by http.service setToken).
 * Public files (car photos, reports, quotations, the unsigned VSO) keep their open links.
 */
export const isPrivateFileUrl = (url) =>
  typeof url === "string" && /\/api\/files\/\d+\/[^/]+$/.test(url.split("?")[0]);

/** The file behind a link, as a Blob; with the login when it is a private document. */
export async function loadFileBlob(url) {
  if (isPrivateFileUrl(url)) {
    try {
      // Loaded here rather than at the top: the test runner cannot parse axios, and the
      // purchase screens that import this file are tested.
      const { default: axios } = await import("axios");
      const res = await axios.get(url, { responseType: "blob" });
      return res.data;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) throw new Error("You do not have access to this document.");
      if (status === 404) throw new Error("This document could not be found.");
      throw e;
    }
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch: ${res.status} ${res.statusText}`);
  return res.blob();
}

// One object URL per link for the session, so a preview that re-renders does not download
// the same document again.
const objectUrlCache = new Map();
const MAX_CACHED = 100;

/** A URL for <img src>, <iframe> or <a href>: public links as they are, private ones
 *  downloaded with the login once and cached. */
export async function fileObjectUrl(url) {
  if (!isPrivateFileUrl(url)) return url;
  if (objectUrlCache.has(url)) return objectUrlCache.get(url);
  const objectUrl = URL.createObjectURL(await loadFileBlob(url));
  if (objectUrlCache.size >= MAX_CACHED) {
    const [oldestKey, oldestUrl] = objectUrlCache.entries().next().value;
    URL.revokeObjectURL(oldestUrl);
    objectUrlCache.delete(oldestKey);
  }
  objectUrlCache.set(url, objectUrl);
  return objectUrl;
}

/** Open a file in a new tab. A private document is opened from memory, since a new tab does
 *  not send the login. */
export async function openFile(url) {
  if (!isPrivateFileUrl(url)) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  // open the tab while the click still counts as a user action, so it is not blocked
  const tab = window.open("", "_blank");
  try {
    const objectUrl = await fileObjectUrl(url);
    if (tab) tab.location.href = objectUrl;
    else window.open(objectUrl, "_blank");
  } catch (e) {
    if (tab) tab.close();
    throw e;
  }
}

/** onClick for an <a href> to a file: public links open as before, a private document is
 *  opened from memory (a plain new tab would not carry the login). */
export function openIfPrivate(event, url) {
  if (!isPrivateFileUrl(url)) return;
  event.preventDefault();
  openFile(url).catch(() => {});
}

/** Save a file under its name. A private document is downloaded with the login; the download
 *  attribute alone does nothing for a link on another origin. */
export async function downloadFile(url, name) {
  const blob = await loadFileBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = name || "";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

/** onClick for a download <a>: a public link downloads as before, a private one with the login. */
export function downloadIfPrivate(event, url, name) {
  if (!isPrivateFileUrl(url)) return;
  event.preventDefault();
  downloadFile(url, name).catch(() => openFile(url).catch(() => {}));
}
